import { useTeamError, useTeamLabels } from '@/components/team/labels'
import { PersonAvatar } from '@/components/team/PersonAvatar'
import {
  useChangeRole,
  useJobOptions,
  useRemovePerson,
  useSaveJobs,
  useTeamPerson,
} from '@/hooks/useTeam'
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  CheckboxGroup,
  NativeSelect,
} from '@repo/ui/components'
import { formatPhoneNumber } from '@repo/utils'
import {
  ROSTER_ASSIGNABLE_ROLES,
  rosterRoleSchema,
  type RosterJobOption,
  type RosterPerson,
} from '@repo/zod-schemas/supervisor-roster'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Briefcase, Info, Loader2, Trash2, UserX } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

export const Route = createFileRoute('/_contractor/team/$userId')({
  component: TeamPersonPage,
})

const assignedIds = (jobs: RosterJobOption[]) =>
  jobs.filter((job) => job.assigned).map((job) => job.projectId)

const sameSet = (a: string[], b: string[]) => {
  const set = new Set(b)
  return a.length === b.length && a.every((id) => set.has(id))
}

function DetailRow({ label, value }: { label: string; value: string | null }) {
  if (!value) return null
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right break-all">{value}</span>
    </div>
  )
}

function RoleCard({ person }: { person: RosterPerson }) {
  const { t } = useTranslation('team')
  const labels = useTeamLabels()
  const changeRole = useChangeRole()
  const teamError = useTeamError()

  const handleChange = async (value: string) => {
    const role = rosterRoleSchema.safeParse(value)
    if (!role.success || role.data === person.role) return
    try {
      await changeRole.mutateAsync({ userId: person.userId, role: role.data })
      toast.success(t('person.roleChanged'))
    } catch (err) {
      toast.error(teamError(err))
    }
  }

  const lockedReason = person.isSelf ? t('person.roleLocked.self') : t('person.roleLocked.manager')

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('person.role')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {person.manageable ? (
          <>
            <NativeSelect
              aria-label={t('person.role')}
              value={person.role}
              disabled={changeRole.isPending}
              onChange={(e) => void handleChange(e.target.value)}
            >
              {ROSTER_ASSIGNABLE_ROLES.map((role) => (
                <option key={role} value={role}>
                  {t(`role.${role}`)}
                </option>
              ))}
            </NativeSelect>
            <p className="text-sm text-muted-foreground">
              {t(`roleDescription.${person.role}`, { defaultValue: '' })}
            </p>
          </>
        ) : (
          <>
            <p className="font-medium">{labels.role(person.role)}</p>
            <p className="text-sm text-muted-foreground">{lockedReason}</p>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function JobChecklist({ person, jobs }: { person: RosterPerson; jobs: RosterJobOption[] }) {
  const { t } = useTranslation('team')
  const labels = useTeamLabels()
  const save = useSaveJobs()
  const teamError = useTeamError()

  // Null until the first tick, so a refetch that lands before any edit still
  // shows the server's answer rather than a stale copy of it.
  const [picked, setPicked] = useState<string[] | null>(null)
  const initial = assignedIds(jobs)
  const selected = picked ?? initial

  const handleSave = async () => {
    try {
      await save.mutateAsync({ userId: person.userId, projectIds: selected })
      setPicked(null)
      toast.success(t('jobs.saved'))
    } catch (err) {
      toast.error(teamError(err))
    }
  }

  if (jobs.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <Briefcase className="size-8 text-muted-foreground" />
        <p className="font-medium">{t('jobs.empty.title')}</p>
        <p className="text-sm text-muted-foreground">{t('jobs.empty.description')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <CheckboxGroup
        aria-label={t('jobs.title')}
        value={selected}
        onChange={setPicked}
        isDisabled={save.isPending}
        className="max-h-96 space-y-1 overflow-y-auto rounded-md border p-2"
      >
        {jobs.map((job) => (
          <Checkbox key={job.projectId} value={job.projectId} className="rounded-sm px-2 py-1.5">
            <span className="flex min-w-0 flex-col">
              <span className="font-medium">{job.name}</span>
              <span className="truncate text-xs text-muted-foreground">{labels.jobLine(job)}</span>
            </span>
          </Checkbox>
        ))}
      </CheckboxGroup>

      <Alert>
        <Info className="size-4" />
        <AlertDescription>
          <p>{t('jobs.note', { name: person.givenName || labels.name(person) })}</p>
          {person.role === 'contractor_member' && <p>{t('jobs.memberNote')}</p>}
        </AlertDescription>
      </Alert>

      <div className="flex justify-end">
        <Button
          onPress={() => void handleSave()}
          isDisabled={save.isPending || sameSet(selected, initial)}
        >
          {save.isPending && <Loader2 className="size-4 animate-spin" />}
          {t('jobs.save')}
        </Button>
      </div>
    </div>
  )
}

function JobsCard({ person }: { person: RosterPerson }) {
  const { t } = useTranslation('team')
  const labels = useTeamLabels()
  const options = useJobOptions(person.userId)

  const renderContent = () => {
    // A master's jobs are the office's to manage (`SUP-41`), so only list them.
    if (!person.manageable) {
      return (
        <div className="space-y-3">
          {person.jobs.length > 0 ? (
            <ul className="space-y-2">
              {person.jobs.map((job) => (
                <li key={job.projectId} className="text-sm">
                  <p className="font-medium">{job.name}</p>
                  <p className="text-xs text-muted-foreground">{labels.jobLine(job)}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t('list.noJobs')}</p>
          )}
          <p className="text-sm text-muted-foreground">{t('jobs.lockedNote')}</p>
        </div>
      )
    }
    if (options.isLoading) {
      return (
        <div className="flex justify-center py-8">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      )
    }
    if (!options.data) {
      return (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <p className="text-sm text-muted-foreground">{t('jobs.error.title')}</p>
          <Button variant="outline" size="sm" onPress={() => void options.refetch()}>
            {t('list.retry')}
          </Button>
        </div>
      )
    }
    return <JobChecklist person={person} jobs={options.data.jobs} />
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('jobs.title')}</CardTitle>
      </CardHeader>
      <CardContent>{renderContent()}</CardContent>
    </Card>
  )
}

function RemoveButton({ person }: { person: RosterPerson }) {
  const { t } = useTranslation(['team', 'common'])
  const navigate = useNavigate()
  const labels = useTeamLabels()
  const remove = useRemovePerson()
  const teamError = useTeamError()
  const name = labels.name(person)

  const handleRemove = async () => {
    try {
      await remove.mutateAsync(person.userId)
      toast.success(t('person.removed', { name }))
      void navigate({ to: '/team' })
    } catch (err) {
      toast.error(teamError(err))
    }
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className="text-destructive" isDisabled={remove.isPending}>
          {remove.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Trash2 className="size-4" />
          )}
          {t('person.remove')}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('person.removeConfirmTitle', { name })}</AlertDialogTitle>
          <AlertDialogDescription>{t('person.removeConfirmBody')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-white hover:bg-destructive/90"
            onClick={() => void handleRemove()}
          >
            {t('person.remove')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function PersonBody({ person }: { person: RosterPerson }) {
  const { t } = useTranslation('team')
  const labels = useTeamLabels()

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <PersonAvatar person={person} className="size-14 text-base" />
          <div>
            <h1 className="text-2xl font-bold">{labels.name(person)}</h1>
            <p className="text-muted-foreground">
              {labels.role(person.role)} · {labels.status(person)}
            </p>
          </div>
        </div>
        {person.manageable && <RemoveButton person={person} />}
      </header>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('person.contact')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <DetailRow label={t('person.email')} value={person.email} />
              <DetailRow
                label={t('person.phone')}
                value={person.phone ? (formatPhoneNumber(person.phone) ?? person.phone) : null}
              />
            </CardContent>
          </Card>
          <RoleCard person={person} />
        </div>
        <JobsCard person={person} />
      </div>
    </div>
  )
}

function TeamPersonPage() {
  const { t } = useTranslation('team')
  const { userId } = Route.useParams()
  const { query, person } = useTeamPerson(userId)

  const renderContent = () => {
    if (query.isLoading) {
      return (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      )
    }
    if (!person) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
          <UserX className="size-12 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {query.isError ? t('list.error.title') : t('person.notFound')}
          </p>
        </div>
      )
    }
    return <PersonBody person={person} />
  }

  return (
    <div className="space-y-6">
      <Link
        to="/team"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('person.back')}
      </Link>
      {renderContent()}
    </div>
  )
}
